
import React, { useEffect, useRef } from "react";

// A color palette for each time of day (HEX values)
const TIME_OF_DAY_COLORS = {
  morning: [
    "#fa709a", // Rose
    "#fee140", // Yellow
    "#c471f5", // Purple
    "#43cea2"  // Greenish-Blue
  ],
  afternoon: [
    "#36d1c4", // Turquoise/Cyan
    "#f7797d", // Sunset
    "#ffcb57", // Orange
    "#90e0ff"  // Blue
  ],
  evening: [
    "#654ea3", // Violet
    "#eaafc8", // Light Purple Pink
    "#ff333d", // Neon Red
    "#2b5876"  // Deep Blue
  ],
  night: [
    "#232526", // Charcoal
    "#6a11cb", // Indigo
    "#2575fc", // Blue
    "#40306A"  // Moody Purple
  ],
};

export interface GradientBackgroundProps {
  timeSlot: keyof typeof TIME_OF_DAY_COLORS;
  className?: string;
}

const GradientBackground: React.FC<GradientBackgroundProps> = ({
  timeSlot,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Function to set CSS vars for canvas gradient
  function setCssVars(colors: string[]) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    colors.forEach((color, idx) => {
      canvas.style.setProperty(`--gradient-color-${idx + 1}`, color);
    });
  }

  useEffect(() => {
    // grab colors for this time slot
    const colors = TIME_OF_DAY_COLORS[timeSlot] || TIME_OF_DAY_COLORS.morning;
    setCssVars(colors);

    // MiniGl, Gradient etc are ported "inside" useEffect to run per-mount
    // Adapted from https://codepen.io/ashik4339/pen/XWNmOrm
    let raf: number;
    let gradient: any = null;

    // ---- Minimal version of the CodePen logic starts here ----
    function normalizeColor(hexCode: string) {
      const n = hexCode.replace("#", "");
      const num = parseInt(n, 16);
      return [
        ((num >> 16) & 255) / 255,
        ((num >> 8) & 255) / 255,
        (num & 255) / 255,
      ];
    }

    let canvas = canvasRef.current!;
    let gl = canvas.getContext("webgl", { antialias: true });

    // Responsive canvas size
    function resize() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl?.viewport(0, 0, canvas.width, canvas.height);
    }
    resize();
    window.addEventListener("resize", resize);

    function getCSSColorVars() {
      const s = getComputedStyle(canvas);
      return [
        s.getPropertyValue("--gradient-color-1").trim() || "#a960ee",
        s.getPropertyValue("--gradient-color-2").trim() || "#ff333d",
        s.getPropertyValue("--gradient-color-3").trim() || "#90e0ff",
        s.getPropertyValue("--gradient-color-4").trim() || "#ffcb57",
      ];
    }

    // SHADERS
    const vertexShaderSrc = `
      attribute vec2 position;
      varying vec2 vUv;

      void main() {
        vUv = (position + 1.0) * 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;
    const fragmentShaderSrc = `
      precision highp float;
      uniform float u_time;
      uniform vec2 u_resolution;
      uniform vec3 u_color1;
      uniform vec3 u_color2;
      uniform vec3 u_color3;
      uniform vec3 u_color4;
      varying vec2 vUv;

      float noise(vec2 p) {
        return fract(sin(dot(p ,vec2(12.9898,78.233))) * 43758.5453);
      }

      // stripe+blend
      void main() {
        vec2 uv = vUv;
        // animate stripes
        float t = u_time * 0.12;
        float stripes = sin((uv.y + t) * 12.0 + sin(uv.x * 7.0 + t * 2.5) * 1.5);

        // blend 2-color background - soft moody wash
        vec3 bg = mix(u_color1, u_color2, uv.y);

        // overlay stripes color
        vec3 stripeColor = mix(u_color3, u_color4, (stripes + 1.0) * 0.5);
        // Wavy brightness mask for moodiness
        float mask = 0.4 + 0.6 * pow(sin((uv.x + t * 0.3) * 6.0 + uv.y * 4.3 + cos(t) * 2.0),2.0);
        stripeColor = mix(stripeColor, bg, 0.12 + 0.25 * mask);

        // Subtle random noise for organic vibe
        float n = noise(uv * 120. + t * 3. + stripes * 0.5);

        // Overlay blend (soft shadow gradient at the top/bottom)
        float vignette = smoothstep(1.0, 0.79, uv.y) * 0.29 + smoothstep(0.0, 0.21, uv.y) * 0.29;

        vec3 col = mix(bg, stripeColor, 0.33 + 0.42 * (stripes * 0.6 + 0.5) + n * 0.08);
        col = mix(col, vec3(0.08,0.06,0.13), vignette);
        gl_FragColor = vec4(col, 1.0);
      }
    `;

    function createShader(type: number, source: string) {
      const shader = gl!.createShader(type)!;
      gl!.shaderSource(shader, source);
      gl!.compileShader(shader);
      if (!gl!.getShaderParameter(shader, gl!.COMPILE_STATUS)) {
        throw new Error(gl!.getShaderInfoLog(shader)!);
      }
      return shader;
    }

    // Set up program
    const vert = createShader(gl!.VERTEX_SHADER, vertexShaderSrc);
    const frag = createShader(gl!.FRAGMENT_SHADER, fragmentShaderSrc);

    const program = gl!.createProgram()!;
    gl!.attachShader(program, vert);
    gl!.attachShader(program, frag);
    gl!.linkProgram(program);

    // Attributes
    const posLoc = gl!.getAttribLocation(program, "position");

    // Quad geometry
    const vertices = new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      1, 1,
    ]);
    const vertexBuffer = gl!.createBuffer();
    gl!.bindBuffer(gl!.ARRAY_BUFFER, vertexBuffer);
    gl!.bufferData(gl!.ARRAY_BUFFER, vertices, gl!.STATIC_DRAW);

    // Uniforms
    const u_time = gl!.getUniformLocation(program, "u_time");
    const u_resolution = gl!.getUniformLocation(program, "u_resolution");
    const u_color1 = gl!.getUniformLocation(program, "u_color1");
    const u_color2 = gl!.getUniformLocation(program, "u_color2");
    const u_color3 = gl!.getUniformLocation(program, "u_color3");
    const u_color4 = gl!.getUniformLocation(program, "u_color4");

    // Animation loop
    let start = Date.now();
    function render() {
      gl!.viewport(0, 0, canvas.width, canvas.height);
      gl!.useProgram(program);

      // Pass time
      gl!.uniform1f(u_time, (Date.now() - start) / 1000.0);
      gl!.uniform2f(u_resolution, canvas.width, canvas.height);

      // Fetch latest palette (if user switches theme etc)
      const cVars = getCSSColorVars();
      const c1 = normalizeColor(cVars[0]);
      const c2 = normalizeColor(cVars[1]);
      const c3 = normalizeColor(cVars[2]);
      const c4 = normalizeColor(cVars[3]);
      gl!.uniform3fv(u_color1, c1);
      gl!.uniform3fv(u_color2, c2);
      gl!.uniform3fv(u_color3, c3);
      gl!.uniform3fv(u_color4, c4);

      gl!.bindBuffer(gl!.ARRAY_BUFFER, vertexBuffer);
      gl!.enableVertexAttribArray(posLoc);
      gl!.vertexAttribPointer(posLoc, 2, gl!.FLOAT, false, 0, 0);
      gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);

      raf = requestAnimationFrame(render);
    }

    render();

    // Cleanup
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
      gl = null;
    };

    // ---- End ported CodePen
  }, [timeSlot]);

  // CSS for fullpage and high z-index, no pointer events
  return (
    <canvas
      ref={canvasRef}
      className={
        `fixed inset-0 -z-10 w-full h-full block transition-opacity duration-800 ${className}`
      }
      id="gradient-canvas"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: -10,
      }}
    />
  );
};

export default GradientBackground;
